import type { CSSProperties } from 'react';

/* ---------------------------------------------------------------------------
 * The signed-in home, dressed to docs/design/ldg-hub-home-concepts.design.html.
 *
 * The mockup's home is not a card. It is a full-width page with a gutter, a
 * poster-sized greeting, and sections separated by a tracked-out label and a
 * hairline that fades out to the right. cardStyle below is still what every
 * SIGNED-OUT surface uses (sign in, verify email, pending approval), because
 * those really are one small card floating on the page and the mockup does not
 * cover them.
 * ------------------------------------------------------------------------ */

/**
 * Replaces cardStyle on the role home screens. Left aligned, because the
 * mockup is: centred text stops being readable the moment a line wraps, and
 * every one of these sections is a list.
 *
 * 480 rather than the mockup's 390 so a large phone gets a little air without
 * the layout turning into a desktop grid. Bottom padding clears the assistant
 * FAB that GlobalChrome floats over this page.
 */
export const homeFrameStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-4)',
  width: '100%',
  maxWidth: 480,
  /* Horizontal gutter comes from containerStyle; adding it here too would
     double it. This only clears the floating assistant button at the bottom. */
  padding: '0 0 96px',
  textAlign: 'left',
};

/** The tracked-out date line above the greeting. */
export const dateLineStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 11,
  lineHeight: 1,
  fontWeight: 500,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  // Season accent when a holiday window is live (theme.css); the quiet
  // ghost ink the rest of the year.
  color: 'var(--season-accent-text, var(--home-accent-text, var(--ink-ghost)))',
};

/**
 * The big one. Anton, uppercase, 0.86 leading so the two lines lock together.
 *
 * Weight 400 is not a style choice, it is the only cut Anton ships. Asking for
 * bold here gets a synthetic smear, so do not "fix" this to 700.
 */
export const posterGreetingStyle: CSSProperties = {
  margin: '12px 0 0',
  fontFamily: 'var(--font-poster)',
  fontSize: 'var(--text-poster-xl)',
  lineHeight: 0.86,
  fontWeight: 400,
  letterSpacing: '-0.01em',
  textTransform: 'uppercase',
  color: 'var(--ink)',
  overflowWrap: 'anywhere',
};

/**
 * The word the greeting lands on, in green.
 *
 * --lime-deep, NOT --studio-green. The bright brand lime is 2.03:1 on this
 * page and would be the single least readable word on the home screen. This is
 * the whole reason the palette carries two greens.
 */
export const posterAccentStyle: CSSProperties = {
  // Season accent when a holiday window is live (theme.css); brand green
  // the rest of the year.
  color: 'var(--season-accent-text, var(--home-accent-text, var(--lime-deep)))',
};

/** The quiet line under the greeting: who you are, and what is waiting. */
export const greetingSubStyle: CSSProperties = {
  margin: '10px 0 0',
  fontFamily: 'var(--font-body)',
  fontSize: 17,
  lineHeight: 1.4,
  fontWeight: 500,
  color: 'var(--ink-soft)',
};

/** Section header row: tracked label, then a rule that fades out. */
export const sectionHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  margin: '4px 0 0',
};

export const sectionHeaderLabelStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 11,
  lineHeight: 1,
  fontWeight: 800,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  // Brand green ALL year, deliberately not the season accent. Section rules
  // repeat down the whole page, and accenting every one of them is exactly
  // the "all over" Bobbie ruled out (2026-08-17 evening: "Soft touch of red
  // and pink for holidays is great, just not all over"). The seasonal touch
  // stays confined to the greeting moment: the date line and the poster's
  // accent word above.
  color: 'var(--lime-deep)',
  flexShrink: 0,
};

export const sectionHeaderRuleStyle: CSSProperties = {
  flex: 1,
  height: 1,
  background: 'var(--home-accent-line-fade, var(--rule))',
};

export const containerStyle: CSSProperties = {
  minHeight: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--space-6) var(--space-4)',
  textAlign: 'center',
  gap: 'var(--space-4)',
};

export const heroStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'var(--space-3)',
  textAlign: 'center',
};

export const heroLogoWrapStyle: CSSProperties = {
  display: 'inline-block',
  borderRadius: 'var(--radius-md)',
  lineHeight: 0,
};

export const heroLogoImgStyle: CSSProperties = {
  display: 'block',
  width: 'clamp(200px, 64vw, 268px)',
  height: 'auto',
};

export const heroTaglineStyle: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-body)',
  fontSize: 'var(--text-body)',
  color: 'var(--silver-deep)',
  maxWidth: 320,
};

export const eyebrowStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  fontFamily: 'var(--font-body)',
  fontSize: 'var(--text-eyebrow)',
  fontWeight: 800,
  letterSpacing: 1.5,
  textTransform: 'uppercase',
  color: 'var(--silver-deep)',
};

export const compactLogoImgStyle: CSSProperties = {
  display: 'block',
  height: 30,
  width: 'auto',
};

export const noteStyle: CSSProperties = {
  marginTop: 'var(--space-5)',
  padding: 'var(--space-3) var(--space-4)',
  background: 'var(--lime-pale)',
  border: '1px solid var(--lime)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--success-text)',
  fontSize: 13,
  maxWidth: 360,
};

export const statusOkStyle: CSSProperties = {
  padding: 'var(--space-3) var(--space-4)',
  background: 'var(--lime-wash)',
  border: '1px solid var(--lime)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--success-text)',
  fontSize: 14,
  maxWidth: 360,
  fontFamily: 'var(--font-body)',
};

export const statusFailStyle: CSSProperties = {
  padding: 'var(--space-3) var(--space-4)',
  background: '#fff0f0',
  border: '1px solid var(--rose)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--rose-deep)',
  fontSize: 14,
  lineHeight: 1.4,
  maxWidth: 360,
  fontFamily: 'var(--font-body)',
  textAlign: 'left',
};

export const primaryBtnStyle: CSSProperties = {
  background: 'var(--btn-primary-bg)',
  color: '#0E1A0A',
  fontWeight: 800,
  fontSize: 16,
  padding: '0 var(--space-5)',
  minHeight: 'var(--tap-target)',
  borderRadius: 'var(--radius-md)',
  border: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--space-2)',
  boxShadow: 'var(--shadow-md)',
  cursor: 'pointer',
  width: '100%',
};

export const secondaryBtnStyle: CSSProperties = {
  background: 'transparent',
  color: 'var(--ink-soft)',
  fontSize: 13,
  padding: '0 var(--space-3)',
  minHeight: 36,
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
};

export const linkBtnStyle: CSSProperties = {
  background: 'transparent',
  color: 'var(--lime)',
  fontSize: 13,
  fontWeight: 700,
  padding: 0,
  border: 0,
  cursor: 'pointer',
  textDecoration: 'underline',
};

export const cardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
  padding: 'var(--space-5)',
  background: 'var(--white)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-md)',
  width: '100%',
  maxWidth: 360,
};

export const inputStyle: CSSProperties = {
  font: 'inherit',
  fontSize: 16,
  padding: '0 var(--space-3)',
  minHeight: 'var(--tap-target)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--white)',
  color: 'var(--ink)',
  width: '100%',
};

export const dividerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  color: 'var(--ink-ghost)',
  fontSize: 12,
  margin: 'var(--space-1) 0',
};

export const dividerLineStyle: CSSProperties = {
  flex: 1,
  height: 1,
  background: 'var(--line)',
};

export const homeTaskListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginTop: 'var(--space-2)',
  textAlign: 'left',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--off-white)',
  padding: 'var(--space-2)',
};

export const homeTaskRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  padding: '6px 8px',
  textDecoration: 'none',
  color: 'inherit',
  borderRadius: 'var(--radius-sm)',
};

export const homeTaskHeadingStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--ink-soft)',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  margin: '0 0 4px 0',
};
